import React, { useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { Photo, PHOTO_TYPE_LABELS } from '../../../types/annotation';

interface PhotoCarouselProps {
  photos: Photo[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
  fillHeight?: boolean;
}

const PhotoCarousel: React.FC<PhotoCarouselProps> = ({
  photos,
  currentIndex,
  onIndexChange,
  fillHeight = false,
}) => {
  const total = photos.length;
  const current = photos[currentIndex];

  const goBack = useCallback(() => {
    if (currentIndex > 0) onIndexChange(currentIndex - 1);
  }, [currentIndex, onIndexChange]);

  const goForward = useCallback(() => {
    if (currentIndex < total - 1) onIndexChange(currentIndex + 1);
  }, [currentIndex, total, onIndexChange]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goBack();
      if (e.key === 'ArrowRight') goForward();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goBack, goForward]);

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 bg-gray-50 rounded-xl border border-gray-200">
        <ImageOff className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm text-gray-400">No photos</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Image area */}
      <div className="relative w-full bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center" style={{ minHeight: 200 }}>
        {current?.url ? (
          <img
            src={current.url}
            alt={current.photoType ? PHOTO_TYPE_LABELS[current.photoType] : 'Photo'}
            className={`w-full h-full object-contain ${fillHeight ? 'max-h-[60vh]' : 'max-h-72'}`}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-48 gap-2">
            <ImageOff className="w-8 h-8 text-gray-500" />
            <p className="text-sm text-gray-400">
              {current?.photoType ? PHOTO_TYPE_LABELS[current.photoType] : 'No preview'}
            </p>
          </div>
        )}

        {/* Prev/Next overlays */}
        {currentIndex > 0 && (
          <button
            type="button"
            onClick={goBack}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
        {currentIndex < total - 1 && (
          <button
            type="button"
            onClick={goForward}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
            aria-label="Next photo"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Footer: counter + type */}
      <div className="flex items-center justify-between w-full px-1">
        <span className="text-xs text-gray-500 font-medium">
          {currentIndex + 1} of {total}
        </span>
        {current?.photoType && (
          <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-medium">
            {PHOTO_TYPE_LABELS[current.photoType]}
          </span>
        )}
      </div>

      {/* Dot indicators */}
      {total > 1 && (
        <div className="flex gap-1.5">
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onIndexChange(i)}
              className={[
                'w-2 h-2 rounded-full transition-colors',
                i === currentIndex ? 'bg-violet-600' : 'bg-gray-300 hover:bg-gray-400',
              ].join(' ')}
              aria-label={`Go to photo ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoCarousel;
